import { Icon } from "@iconify/react/dist/iconify.js";
import { useState, useEffect } from "react";

/**
 * AuthLoadingOverlay - Shows during Auth0 authentication process
 * Displays when user is being redirected to/from Auth0 Universal Login
 */
const AuthLoadingOverlay = ({ isVisible, message = "Authenticating..." }) => {
  const [dots, setDots] = useState("");
  const [progress, setProgress] = useState(0);

  // Animated dots effect
  useEffect(() => {
    if (!isVisible) return;

    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === "...") return "";
        return prev + ".";
      });
    }, 500);

    return () => clearInterval(interval);
  }, [isVisible]);

  // Progress bar animation
  useEffect(() => {
    if (!isVisible) {
      setProgress(0);
      return;
    }

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return 90; // Stop at 90% until auth completes
        return prev + Math.random() * 15;
      });
    }, 200);

    return () => clearInterval(interval);
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm">
      <div className="bg-neo-card p-8 rounded-lg shadow-2xl max-w-md w-full mx-4 animate-fade-in-scale">
        
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="relative">
            <Icon 
              icon="mdi:shield-check" 
              className="text-6xl text-primary animate-pulse"
            />
            <div className="absolute inset-0 animate-ping">
              <Icon 
                icon="mdi:shield-check-outline" 
                className="text-6xl text-primary opacity-30"
              />
            </div>
          </div>
        </div>

        {/* Loading Message */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-semibold text-white mb-2">
            {message}{dots}
          </h3>
          <p className="text-gray-300 text-sm">
            Please wait while we securely authenticate you
          </p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-gray-700 rounded-full h-2 mb-4 overflow-hidden">
          <div 
            className="bg-gradient-to-r from-primary to-blue-400 h-2 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Spinning Icon */}
        <div className="flex justify-center">
          <Icon 
            icon="eos-icons:loading" 
            className="text-3xl text-primary animate-spin"
          />
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400">
            <Icon icon="mdi:lock" className="text-sm" />
            <span>Secured by Auth0</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthLoadingOverlay;
